import { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import { Link } from "react-router-dom";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong } from "@fortawesome/pro-regular-svg-icons";

import UserContext from "../../../state/UserContext";
import AppRoutes from "../../../routes/AppRoutes";
import AdvancedToDoList from "./advanced-to-do-list/AdvancedToDoList";
import StandardToDoList from "./standard-to-do-list/StandardToDoList";
import TaskTypeSelector from "./TaskTypeSelector";
import { JourneyFormGroupDto } from "../../../types/dtos/journeys";
import ToDoListTaskTypeDto from "../../../types/dtos/dashboards/right-bar/to-do-list/ToDoListTaskTypeDto";
import AdvancedToDoListItemDto from "../../../types/dtos/dashboards/right-bar/to-do-list/AdvancedToDoListItemDto";
import StandardToDoListItemDto from "../../../types/dtos/dashboards/right-bar/to-do-list/StandardToDoListItemDto";
import ModifyTaskResponseDto from "../../../types/dtos/tasks/ModifyTaskResponseDto";
import { ClientTaskType } from "../../../types/dtos/tasks/advanced-tasks/ClientTaskType";

interface RightBarTasksListProps {
  toDoListTaskTypes: ToDoListTaskTypeDto[];
  standardListItems: StandardToDoListItemDto[];
  advancedListItems: AdvancedToDoListItemDto[];
  formDetails: JourneyFormGroupDto | undefined | null;
  hasJourneyBeenSubmitted: boolean;
  modifyStandardTaskFromDashboard(
    task: any,
    onSuccess: (taskData: ModifyTaskResponseDto<number>) => void,
  ): void;
  onLoadStandardTaskToEdit(
    taskId: number,
    taskType: string,
    successCallback: (responseData: any) => void,
  ): void;
}

function getInitialSelectedTaskTypeId(
  taskTypes: ToDoListTaskTypeDto[],
): string | number | undefined {
  if (taskTypes.length === 0) {
    return undefined;
  }
  if (taskTypes.some((x) => x.standardTaskTypeId != null)) {
    return taskTypes[0].standardTaskTypeId ?? undefined;
  }
  return taskTypes[0].advancedTaskTypeId ?? undefined;
}

function isTaskTypeSelected(
  taskType: ToDoListTaskTypeDto,
  selectedId: string | number,
) {
  return (
    selectedId === taskType.standardTaskTypeId ||
    selectedId === taskType.advancedTaskTypeId
  );
}

function getTaskTypeTabId(taskType: ToDoListTaskTypeDto): string {
  const id = taskType.standardTaskTypeId ?? taskType.advancedTaskTypeId;
  return `task-type-tab-${id}`;
}

function findClientTaskType(
  taskTypes: ClientTaskType[],
  selectedId: string | number | undefined,
) {
  if (selectedId == null) {
    return undefined;
  }
  const normalizedSelectedId = String(selectedId).toLowerCase();
  return taskTypes.find(
    (taskType) => String(taskType.id).toLowerCase() === normalizedSelectedId,
  );
}

/** Personal to-do list card in the dashboard right bar (standard or advanced task types). */
function RightBarTasksList({
  toDoListTaskTypes: initialToDoListTaskTypes,
  standardListItems,
  advancedListItems,
  formDetails,
  hasJourneyBeenSubmitted,
  modifyStandardTaskFromDashboard,
  onLoadStandardTaskToEdit,
}: RightBarTasksListProps) {
  const userContext = useContext(UserContext);
  const usesAdvancedTaskTypes = userContext.user.client.taskTypes.length > 0;

  const [selectedTaskTypeId, setSelectedTaskTypeId] = useState<
    string | number | undefined
  >(() => getInitialSelectedTaskTypeId(initialToDoListTaskTypes));
  // Local copy so tab counts can update after add/edit without a full dashboard reload.
  const [toDoListTaskTypes, setToDoListTaskTypes] = useState<
    ToDoListTaskTypeDto[]
  >(initialToDoListTaskTypes);
  const [isTasksLoading, setIsTasksLoading] = useState(false);

  // Keep selection in sync if the dashboard payload task-type list changes.
  useEffect(() => {
    setSelectedTaskTypeId((current) => {
      if (
        current != null &&
        toDoListTaskTypes.some(
          (taskType) =>
            taskType.standardTaskTypeId === current ||
            taskType.advancedTaskTypeId === current,
        )
      ) {
        return current;
      }
      return getInitialSelectedTaskTypeId(toDoListTaskTypes);
    });
  }, [toDoListTaskTypes]);

  const onSelectedTaskTypeIdChange = (
    standardTaskTypeId?: number | null,
    advancedTaskTypeId?: string | null,
  ) => {
    if (standardTaskTypeId != null) {
      setSelectedTaskTypeId(standardTaskTypeId);
    } else if (advancedTaskTypeId != null) {
      setSelectedTaskTypeId(advancedTaskTypeId);
    }
  };

  const updateTaskTypeCount = (taskTypeId: string | number, count: number) => {
    setToDoListTaskTypes((prev) =>
      prev.map((taskType) => {
        if (
          taskType.standardTaskTypeId === taskTypeId ||
          taskType.advancedTaskTypeId === taskTypeId
        ) {
          return { ...taskType, count };
        }
        return taskType;
      }),
    );
  };

  // AdvancedToDoList resolves the selected type from client.taskTypes — mirror that here.
  const selectedClientTaskType = findClientTaskType(
    userContext.user.client.taskTypes,
    selectedTaskTypeId,
  );

  const showAdvancedTaskList =
    usesAdvancedTaskTypes && selectedClientTaskType != null;

  // Always offer a full-page link for advanced clients (selected tab, or first task type).
  const viewAllTaskTypeId =
    selectedClientTaskType?.id ?? userContext.user.client.taskTypes[0]?.id;
  const showViewAllLink = usesAdvancedTaskTypes && viewAllTaskTypeId != null;
  const showTaskTypeSelector =
    selectedTaskTypeId != null && toDoListTaskTypes.length > 1;
  const selectedTaskTypeTabId =
    selectedTaskTypeId != null
      ? toDoListTaskTypes.find((taskType) =>
          isTaskTypeSelected(taskType, selectedTaskTypeId),
        )
      : undefined;
  const selectedTabId = selectedTaskTypeTabId
    ? getTaskTypeTabId(selectedTaskTypeTabId)
    : undefined;

  return (
    <div id="to-do-list" className="bg-white rounded-lg p-4">
      <div className="flex flex-row">
        <header
          className={cx(
            "text-lg font-semibold mt-0 mb-2 min-w-0",
            showViewAllLink ? "basis-9/12" : "flex-1",
          )}
        >
          {t("DashboardRightBar.YourToDoList", "Your to-do list")}
        </header>
        {showViewAllLink && (
          <div className="basis-3/12 text-right">
            <Link
              to={AppRoutes.yourJourney.tasks.generatePathRoute(
                viewAllTaskTypeId,
              )}
              className="group inline-flex items-center hover:cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            >
              <span className="text-sm group-hover:underline">
                {t("Common.ViewAll", "View all")}
              </span>
              <FontAwesomeIcon
                icon={faArrowRightLong}
                className="ml-1"
                size="xs"
                aria-hidden="true"
              />
            </Link>
          </div>
        )}
      </div>

      {showTaskTypeSelector && (
        <TaskTypeSelector
          toDoListTaskTypes={toDoListTaskTypes}
          selectedTaskTypeId={selectedTaskTypeId}
          showCount
          tabPanelId="to-do-list-items"
          onSelectedTaskTypeIdChange={onSelectedTaskTypeIdChange}
        />
      )}

      <div
        id="to-do-list-items"
        role="tabpanel"
        aria-labelledby={selectedTabId}
        aria-busy={isTasksLoading}
        className={cx(
          "pt-2 transition-opacity duration-200 ease-out",
          isTasksLoading && "min-h-[6rem]",
        )}
      >
        {!usesAdvancedTaskTypes && typeof selectedTaskTypeId === "number" && (
          <StandardToDoList
            initialToDoItems={standardListItems}
            toDoListTaskTypes={toDoListTaskTypes}
            selectedTaskTypeId={selectedTaskTypeId}
            updateTaskTypeCount={updateTaskTypeCount}
            modifyTaskFromDashboard={modifyStandardTaskFromDashboard}
            onLoadTaskToEdit={onLoadStandardTaskToEdit}
            hideTaskTypeBadge
            onTasksLoadingChange={setIsTasksLoading}
          />
        )}
        {showAdvancedTaskList && selectedClientTaskType && (
          <AdvancedToDoList
            userContext={userContext}
            initialToDoItems={advancedListItems}
            taskTypes={userContext.user.client.taskTypes}
            toDoListTaskTypes={toDoListTaskTypes}
            selectedTaskTypeId={selectedClientTaskType.id}
            formDetails={formDetails}
            hasJourneyBeenSubmitted={hasJourneyBeenSubmitted}
            updateTaskTypeCount={updateTaskTypeCount}
            onTasksLoadingChange={setIsTasksLoading}
          />
        )}
      </div>
    </div>
  );
}

export default RightBarTasksList;
